import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 appearance-none [-webkit-appearance:none] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default:
					'bg-active-gold text-black font-medium shadow-[inset_0_1px_0_hsl(50,100%,88%),inset_0_-1px_0_rgba(0,0,0,0.12)] hover:bg-active-gold/90 active:bg-active-gold/95 active:translate-y-[0.5px]',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline: 'border border-hairline bg-surface-1 hover:bg-white/[0.07] hover:border-white/20 focus:border-white/30 active:border-white/40 hover:text-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-white/[0.05] focus:bg-white/[0.08] active:bg-white/10 hover:text-foreground',
				'ghost-destructive': 'hover:bg-destructive/15 hover:text-destructive',
				link: 'text-primary underline-offset-4 hover:underline',
				field: 'bg-black/20 border border-white/[0.08] hover:bg-white/10 hover:border-white/20 text-foreground'
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 px-3',
				lg: 'h-11 px-8',
				icon: 'h-10 w-10'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
